import { useState, useRef } from "react";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Popper from "@mui/material/Popper";
import Paper from "@mui/material/Paper";
import Fade from "@mui/material/Fade";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import LogoutButton from "./LogoutButton";
import { useAuth } from "../../contexts/AuthContext";

const stringToColor = (string) => {
  let hash = 0;
  for (let i = 0; i < string.length; i++) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  const color = `hsl(${hash % 360}, 65%, 48%)`;
  return color;
};

export default function AccountPopover() {
  const { user, isAdmin } = useAuth();

  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  const handleToggle = () => setOpen((prev) => !prev);
  const handleClose = () => setOpen(false);

  const avatarColor = stringToColor(user?.name || user?.email || "U");

  return (
    <>
      <IconButton
        ref={anchorRef}
        onClick={handleToggle}
        disableRipple
        sx={{
          p: 0.5,
          borderRadius: "50%",
          transition: "transform 0.15s ease",
          "&:hover": {
            transform: "scale(1.04)",
            bgcolor: "transparent",
          },
        }}
      >
        <Box
          sx={{
            p: "2px",
            borderRadius: "50%",
            background: open
              ? `linear-gradient(135deg, ${avatarColor}, #EAEAEA)`
              : "transparent",
            transition: "background 0.2s ease",
          }}
        >
          <Avatar
            src={user?.profileImage}
            alt={user?.name}
            sx={{
              width: 40,
              height: 40,
              fontWeight: 700,
              bgcolor: !user?.profileImage ? avatarColor : "transparent",
              color: "#fff",
              border: "3px solid #fff",
              boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
            }}
          >
            {user?.name?.charAt(0).toUpperCase()}
          </Avatar>
        </Box>
      </IconButton>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-end"
        transition
        modifiers={[
          {
            name: "offset",
            options: { offset: [0, 12] },
          },
        ]}
        sx={{ zIndex: 1300 }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={180}>
            <Box>
              <ClickAwayListener onClickAway={handleClose}>
                <Paper
                  elevation={0}
                  sx={{
                    minWidth: 260,
                    borderRadius: 3,
                    overflow: "hidden",
                    border: "1px solid #EEF0EF",
                    boxShadow:
                      "0 12px 24px -8px rgba(16,24,22,0.16), 0 4px 8px -2px rgba(16,24,22,0.08)",
                  }}
                >
                  {/* Header */}
                  <Box
                    sx={{
                      px: 2.5,
                      pt: 2.5,
                      pb: 2,
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      background:
                        "linear-gradient(180deg, #FAFBFA 0%, #FFFFFF 100%)",
                    }}
                  >
                    <Avatar
                      src={user?.profileImage}
                      alt={user?.name}
                      sx={{
                        width: 44,
                        height: 44,
                        fontWeight: 700,
                        bgcolor: !user?.profileImage
                          ? avatarColor
                          : "transparent",
                        color: "#fff",
                        boxShadow: "0 2px 6px rgba(0,0,0,0.14)",
                      }}
                    >
                      {user?.name?.charAt(0).toUpperCase()}
                    </Avatar>

                    <Box sx={{ minWidth: 0 }}>
                      {user?.name && (
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 700,
                            fontSize: 15,
                            lineHeight: 1.3,
                          }}
                          noWrap
                        >
                          {user.name}
                        </Typography>
                      )}
                      <Typography
                        variant="body2"
                        sx={{ color: "#7A8781", fontSize: 13 }}
                        noWrap
                      >
                        {user?.email}
                      </Typography>
                    </Box>
                  </Box>

                  {user?.["custom:dealerId"] && (
                    <Box sx={{ px: 2.5, pb: 2 }}>
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5,
                          px: 1.25,
                          py: 0.5,
                          borderRadius: 999,
                          bgcolor: "#F1F5F3",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: 0.4,
                            color: "#5B6B62",
                            textTransform: "uppercase",
                          }}
                        >
                          ID
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 12.5,
                            fontWeight: 700,
                            color: "#213024",
                          }}
                        >
                          {user["custom:dealerId"]}
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  <Divider sx={{ borderColor: "#EEF0EF" }} />

                  <Box sx={{ p: 1.5 }}>
                    <LogoutButton
                      showText
                      variant="text"
                      sx={{
                        width: "100%",
                        justifyContent: "flex-start",
                        borderRadius: 2,
                        bgcolor: "#FFF4F4",
                        color: "#C0392B",
                        fontWeight: 600,
                        px: 1.75,
                        py: 1,
                        transition: "background 0.15s ease",
                        ":hover": {
                          bgcolor: "#FBE2E2",
                        },
                      }}
                    />
                  </Box>
                </Paper>
              </ClickAwayListener>
            </Box>
          </Fade>
        )}
      </Popper>
    </>
  );
}
